import axios, { AxiosError } from 'axios';
import { configApiInstance } from './config';
import { refreshSession } from './refreshSession';
import { endSession } from '@/services/endSession';
import { createStandaloneToast } from '@chakra-ui/react';
import { getAccountStateReason, getSharedAccountStateMessage, isTerminalAccountReason } from './accountState';
import { readRefreshGeneration } from './refreshSession';
import { apiPaths } from './paths';
import { queryClient } from '@/services/queryClient';
import queryKeys from '@/constants/queryKeys';

export { getSharedAccountStateMessage } from './accountState';

export type TCustomError = {
  message: string;
  code: number;
  name: string;
  payload: null;
};

export type TCustomAxiosError = {
  code: number;
  data: unknown;
  success: boolean;
  errors: TCustomError[];
  reason?: string;
};

export type TCustomAxiosResponse<T> = {
  code: number;
  success: boolean;
  errors: TCustomError[];
  data: T;
};

export type TCustomAxiosPaginationResponse<T> = TCustomAxiosResponse<T> & {
  pagination: {
    page: number;
    limit: number;
    totalPages: number;
    offset: number;
    totalItems: number;
  };
};

export type TPagination = {
  getFirstPage: () => void;
  getNextPage: () => void;
  getPreviousPage: () => void;
  getLastPage: () => void;
  currentPage: number;
  totalPages: number;
  totalItems: number;
};

export type TInfinitePagination = {
  totalItems: number;
  loadedItems?: number;
  loadedPages?: number;
  totalPages?: number;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
};

const api = axios.create(configApiInstance);
export const { toast: sharedToast, ToastContainer: AccountStateToastContainer } = createStandaloneToast();

api.interceptors.request.use((request) => {
  request._refreshGeneration ??= readRefreshGeneration();
  return request;
});

const withUserMessage = (body: unknown, message: string, status: number): TCustomAxiosError => ({
  ...(body && typeof body === 'object' ? body : {}),
  code: status,
  data: body && typeof body === 'object' && 'data' in body ? body.data : null,
  success: false,
  errors: [{ message, code: status, name: 'AccountStateError', payload: null }],
});

const showSharedMessage = (message: string, id: string) => {
  if (!sharedToast.isActive(id)) {
    sharedToast({ id, status: 'error', title: message, position: 'bottom', duration: 5000, isClosable: true });
  }
};

const waitForRetry = (attempt: number) => new Promise<void>((resolve) => setTimeout(resolve, 300 * 2 ** attempt));

/**
 * A refresh rejected for an invalid token ends the session. A 409 means an
 * account operation or maintenance is in progress and leaves it intact.
 *
 * A network error or a 5xx is an auth-service blip, not a dead session: the
 * request that triggered the refresh fails, but the user stays signed in.
 */
const isRefreshRejected = (error: unknown): boolean => {
  const status = (error as AxiosError)?.response?.status;

  return typeof status === 'number' && status >= 400 && status < 500 && status !== 409;
};

/**
 * Endpoints reached *before* there is a session: sign-in, and the password-reset
 * surface.
 *
 * A 401 from any of these is a bad credential or a bad token — something the form
 * owns and shows — not an expired session. Refreshing on their behalf is
 * meaningless (there is nothing to refresh) and ending a session on their behalf
 * navigates a signed-out user away from the page they are trying to use, losing
 * the reset link they just clicked.
 */
const PRE_AUTH_PATHS = [
  '/login',
  '/forgot-password',
  '/set-password',
  '/reset-password',
  '/verify-token',
  '/verify-reset-token',
];

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // A 401 here is a bad credential or a bad token, owned by the form, not an expiry.
    const isPreAuthRequest = PRE_AUTH_PATHS.some((path) => originalRequest?.url?.includes(path));
    // Opted out of session teardown: this request may refresh and replay, but a
    // failure of it must never sign the user out. Boot's secondary fetches set it,
    // so an outage in one of them degrades instead of ending a valid session.
    const mayEndSession = !originalRequest?.skipSessionTeardown;
    const status = error.response?.status;
    const reason = getAccountStateReason(error);

    if (status === 401 && isTerminalAccountReason(reason)) {
      await endSession({ reason });
      return Promise.reject(error.response.data);
    }

    if (status === 503 && reason === 'ACCOUNT_STATE_UNAVAILABLE') {
      const attempt = originalRequest?._accountStateRetries ?? 0;
      // The API contract forbids replaying either password write: the first
      // attempt may have completed before account state became unavailable.
      const isPasswordSubmission =
        originalRequest?.method?.toLowerCase() === 'post' &&
        (originalRequest.url === apiPaths.user.setInitialPassword || originalRequest.url === apiPaths.user.resetPassword);
      if (originalRequest && !isPasswordSubmission && !originalRequest.skipAccountStateRetry && attempt < 3) {
        originalRequest._accountStateRetries = attempt + 1;
        await waitForRetry(attempt);
        return api(originalRequest);
      }
      const message = getSharedAccountStateMessage(reason)!;
      if (!isPreAuthRequest) showSharedMessage(message, 'account-state-unavailable');
      return Promise.reject(withUserMessage(error.response.data, message, status));
    }

    if (
      status === 503 ||
      (status === 409 && (reason === 'ACCOUNT_OPERATION_IN_PROGRESS' || reason === 'MAINTENANCE_FREEZE'))
    ) {
      const message = status === 409 && reason ? getSharedAccountStateMessage(reason) : undefined;
      const display = message ?? 'The service is temporarily unavailable. Try again shortly.';
      if (!isPreAuthRequest) showSharedMessage(display, `account-state-${reason ?? status}`);
      return Promise.reject(withUserMessage(error.response.data, display, status));
    }

    // One refresh-and-replay per request. The per-request _retry flag is the only
    // thing needed to stop a loop; the module-level retry budget this replaces was
    // shared by every request, so a burst of parallel 401s exhausted it and then
    // forced a sign-out on any later error of any status.
    const shouldTryRefresh = !isPreAuthRequest && !originalRequest?._retry && status === 401;

    if (shouldTryRefresh) {
      originalRequest._retry = true;

      try {
        await refreshSession(originalRequest?._refreshGeneration);
        return api(originalRequest);
      } catch (refreshError) {
        const refreshStatus = (refreshError as AxiosError)?.response?.status;
        const refreshReason = getAccountStateReason(refreshError);
        if (
          refreshStatus === 503 ||
          (refreshStatus === 409 &&
            (refreshReason === 'ACCOUNT_OPERATION_IN_PROGRESS' || refreshReason === 'MAINTENANCE_FREEZE'))
        ) {
          const message =
            refreshReason && getSharedAccountStateMessage(refreshReason)
              ? getSharedAccountStateMessage(refreshReason)!
              : 'The service is temporarily unavailable. Try again shortly.';
          showSharedMessage(message, `account-state-${refreshReason ?? refreshStatus}`);
          return Promise.reject(withUserMessage((refreshError as AxiosError)?.response?.data, message, refreshStatus));
        }
        if (mayEndSession && isRefreshRejected(refreshError)) {
          await endSession({ reason: isTerminalAccountReason(refreshReason) ? refreshReason : 'expired' });
        }

        return Promise.reject(refreshError);
      }
    }

    // The refresh succeeded but the replayed request still came back 401, so the
    // session is genuinely dead rather than merely stale.
    if (!isPreAuthRequest && status === 401 && originalRequest?._retry) {
      await endSession({ reason: 'expired' });
    }

    // A request that never got a response — offline, CORS rejection, DNS failure,
    // timeout, aborted request — has no `error.response`. Reading `.data` off it
    // throws a TypeError *inside the interceptor*, and that TypeError becomes the
    // rejection every caller sees instead of the app's error shape. Reject with a
    // synthesised TCustomAxiosError so `err.errors[0].message` keeps working.
    if (!error.response) {
      return Promise.reject({
        code: 0,
        data: null,
        success: false,
        errors: [
          {
            message: error.message || 'Network request failed.',
            code: 0,
            name: error.name || 'NetworkError',
            payload: null,
          },
        ],
      } satisfies TCustomAxiosError);
    }

    // A super admin's organization viewing context may have been set after this
    // tab loaded (e.g. from another tab). Refetch it so the viewing-mode modal in
    // the admin layout opens. Other READ_ONLY sources simply refetch to null.
    if (status === 403 && error.response?.data?.reason === 'READ_ONLY' && !isPreAuthRequest) {
      queryClient.invalidateQueries({ queryKey: [queryKeys.superAdmin.currentOrganizationContext] });
    }

    const apiError = error.response.data as TCustomAxiosError;
    return Promise.reject(apiError);
  },
);

export { api };
